import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiSend, paystubUrl } from './api';
import type { PaymentMethod, SalaryEntry } from './types';
import Sheet from '../../shared/components/Sheet';
import { useEditProtection } from '../../shared/components/EditProtection';
import { fmtCurrency } from './formatters';

interface Props {
  entry: SalaryEntry;
  onClose: () => void;
  onPaymentRecorded: () => void;
}

interface AdminUser {
  id: number;
  name: string;
  total_cash: string | number;
  role: string;
}

const methodOptions: { value: PaymentMethod; label: string }[] = [
  { value: 'bank', label: 'Bank transfer (no balance impact)' },
  { value: 'etransfer', label: 'E-transfer (no balance impact)' },
  { value: 'till_cash', label: 'Cash from till' },
  { value: 'admin_cash', label: 'Cash from admin holding' },
];

const todayLocalISO = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const RecordPaymentModal: React.FC<Props> = ({ entry, onClose, onPaymentRecorded }) => {
  const { sessionToken } = useAuth();
  const outstanding = Math.max(0, Number(entry.net) - Number(entry.total_paid || 0));
  const [method, setMethod] = useState<PaymentMethod>('bank');
  const [amount, setAmount] = useState(outstanding.toFixed(2));
  const [paidAt, setPaidAt] = useState(todayLocalISO());
  const [confirmation, setConfirmation] = useState('');
  const [notes, setNotes] = useState('');
  const [sourceUserId, setSourceUserId] = useState<number | ''>('');
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ id: number; paystub_number: string | null } | null>(null);

  const [review, setReview] = useState(false);
  useEditProtection(!result);
  const requestClose = () => { if (!submitting && (result || window.confirm('Discard this payment draft?'))) onClose(); };

  useEffect(() => {
    if (method === 'admin_cash') {
      (async () => {
        try {
          const res = await fetch('/api/users', {
            headers: { 'X-Session-Token': sessionToken || '' },
            cache: 'no-store',
          });
          const data = await res.json();
          const list: AdminUser[] = (data.users || []).filter(
            (u: AdminUser) => ['Admin', 'Owner'].includes(u.role) && Number(u.total_cash) > 0
          );
          setAdmins(list);
        } catch (e: any) {
          setErr(e.message);
        }
      })();
    }
  }, [method, sessionToken]);

  const submit = async () => {
    if (!navigator.onLine) { setErr('Reconnect before recording a payment.'); return; }
    if (!Number.isFinite(Number(amount)) || Number(amount) <= 0 || Number(amount) > outstanding || !paidAt) { setErr('Enter a valid date and an amount greater than zero, up to the outstanding balance.'); return; }
    try {
      setSubmitting(true);
      setErr('');
      const body: any = {
        entry_id: entry.id,
        method,
        amount: Number(amount),
        paid_at: paidAt ? `${paidAt}T12:00:00Z` : null,
        confirmation: confirmation || null,
        notes: notes || null,
      };
      if (method === 'admin_cash') {
        if (!sourceUserId) throw new Error('Pick a source admin');
        body.source_user_id = sourceUserId;
      }
      const res = await apiSend<{ payment: { id: number; paystub_number: string | null } }>(
        '/payments',
        'POST',
        body,
        sessionToken
      );
      setResult(res.payment);
      onPaymentRecorded();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Sheet title="Payment recorded" onClose={onClose}>
          <p>
            {fmtCurrency(amount)} recorded for {entry.employee_name}. {entry.is_cash_employee ? 'Cash employees do not receive a pay stub.' : result.paystub_number ? `Pay stub ${result.paystub_number} is ready.` : 'Pay stub not yet available. Generate it from the Payments list.'}
          </p>
          {result.paystub_number && (
            <p>
              <a href={paystubUrl(result.id, sessionToken)} target="_blank" rel="noreferrer" className="salary-btn">
                View / Download Pay Stub (PDF)
              </a>
            </p>
          )}
          <button className="salary-btn secondary" onClick={onClose}>Close</button>
      </Sheet>
    );
  }

  return (
    <Sheet title={`Record payment — ${entry.employee_name}`} onClose={requestClose} busy={submitting}>
        <p style={{ fontSize: '0.9rem', color: '#666' }}>
          Net owed: <strong>{fmtCurrency(entry.net)}</strong>.
          {' '}Already paid: <strong>{fmtCurrency(entry.total_paid || 0)}</strong>.
          {' '}Outstanding: <strong>{fmtCurrency(outstanding)}</strong>.
        </p>

        {err && <div className="error-banner">{err}</div>}

        <fieldset disabled={review || submitting} style={{ border: 0, padding: 0, minWidth: 0 }}><div className="form-row">
          <label htmlFor="payment-method">Method</label>
          <select id="payment-method" disabled={submitting} value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {methodOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {method === 'admin_cash' && (
          <div className="form-row">
            <label htmlFor="payment-source">Source admin</label>
            <select id="payment-source" disabled={submitting} value={sourceUserId} onChange={(e) => setSourceUserId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">-- select --</option>
              {admins.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — {fmtCurrency(a.total_cash)}
                </option>
              ))}
            </select>
            {admins.length === 0 && <small>No admins currently hold cash.</small>}
          </div>
        )}

        <div className="form-row">
          <label htmlFor="payment-amount">Amount</label>
          <input
            id="payment-amount" disabled={submitting} inputMode="decimal" type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <div className="form-row">
          <label htmlFor="payment-date">Pay date</label>
          <input
            id="payment-date" disabled={submitting} type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </div>

        <div className="form-row">
          <label htmlFor="payment-reference">Confirmation number / reference (optional)</label>
          <input id="payment-reference" disabled={submitting} type="text" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
        </div>

        <div className="form-row">
          <label htmlFor="payment-notes">Notes (optional)</label>
          <textarea id="payment-notes" disabled={submitting} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        </fieldset>
        {review && <div role="status"><h4>Review payment</h4><p>{fmtCurrency(amount)} to {entry.employee_name} on {paidAt}.</p><p>{methodOptions.find(o => o.value === method)?.label}{method === 'admin_cash' ? ` — ${admins.find(a => a.id === sourceUserId)?.name || ''}` : ''}</p><p>Only confirm after the transfer or cash handover has occurred.</p><button disabled={submitting} onClick={() => setReview(false)}>Edit details</button></div>}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="salary-btn secondary" onClick={requestClose} disabled={submitting}>Cancel</button>
          <button className="salary-btn" onClick={() => review ? submit() : setReview(true)} disabled={submitting || !Number.isFinite(Number(amount)) || Number(amount) <= 0 || Number(amount) > outstanding || !paidAt || (method === 'admin_cash' && !sourceUserId)}>
            {submitting ? 'Recording…' : review ? 'Confirm payment' : 'Review payment'}
          </button>
        </div>
    </Sheet>
  );
};

export default RecordPaymentModal;
